/**
 * How a valuation was reached: the method, each step with its figures, the assumptions
 * (and which were left at a default), and what was missing.
 *
 * Built by the backend from the same figures the engine used (valuation/v2/explain.py),
 * so it cannot disagree with the number it explains. The steps are numbered because they
 * are a sequence: each one's result is the next one's input.
 */

import clsx from 'clsx';
import PriceScenarios, { type PriceScenario } from './PriceScenarios';

export interface Explanation {
  method: { name: string; why: string };
  steps: { title: string; formula: string; lines: string[] }[];
  assumptions: { label: string; value: string; supplied: boolean; note: string }[];
  missing: string[];
  otherMethods: {
    method: string;
    value: number | null;
    basis?: string | null;
    needs?: string[] | null;
    scenarios?: PriceScenario[];
  }[];
}

export default function ValuationExplanation({ explanation }: { explanation: Explanation | undefined }) {
  if (!explanation) {
    return <p className="text-sm text-gray-500">This valuation has not been computed yet.</p>;
  }
  return (
    <div className="space-y-6">
      <section className="rounded-xl border border-accent-200 bg-accent-50 p-5">
        <div className="text-xs font-semibold uppercase tracking-wide text-accent-700">Method applied</div>
        <h2 className="mt-1 font-display text-lg font-bold text-gray-900">{explanation.method.name}</h2>
        <p className="mt-1.5 text-sm leading-relaxed text-gray-700">{explanation.method.why}</p>
      </section>

      <section>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">How it was applied</h3>
        <ol className="mt-3 space-y-3">
          {explanation.steps.map((step, index) => (
            <li key={step.title} className="flex gap-4 rounded-xl border border-gray-200 bg-white p-4">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-600 text-xs font-bold text-white">
                {index + 1}
              </span>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">{step.title}</div>
                <div className="mt-0.5 text-xs text-gray-600">{step.formula}</div>
                <ul className="mt-2 space-y-0.5 font-mono text-xs text-gray-800">
                  {step.lines.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Assumptions</h3>
        <div className="mt-3 overflow-hidden rounded-xl border border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <tbody className="divide-y divide-gray-100">
              {explanation.assumptions.map((a) => (
                <tr key={a.label}>
                  <td className="px-4 py-2.5 font-medium text-gray-900">{a.label}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-gray-900">{a.value}</td>
                  <td className="px-4 py-2.5">
                    <span
                      className={clsx(
                        'rounded-full px-2 py-0.5 text-[11px] font-medium',
                        a.supplied ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800',
                      )}
                    >
                      {a.supplied ? 'Supplied' : 'Default'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-600">{a.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Missing or limited information</h3>
        {explanation.missing.length === 0 ? (
          <p className="mt-3 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900">
            Nothing missing: every figure the method needs was supplied.
          </p>
        ) : (
          <ul className="mt-3 list-disc space-y-1.5 rounded-xl border border-amber-200 bg-amber-50 py-3 pl-8 pr-4 text-sm text-amber-900">
            {explanation.missing.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        )}
      </section>

      {explanation.otherMethods.length > 0 && (
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Other methods, from supplied inputs</h3>
          <ul className="mt-3 space-y-2">
            {explanation.otherMethods.map((m) => (
              <li key={m.method} className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm">
                <span className="font-medium text-gray-900">{m.method}</span>
                <span className="ml-2 font-mono text-xs text-gray-700">
                  {m.value === null ? 'not computable' : m.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
                {m.basis && <div className="mt-0.5 text-xs text-gray-600">{m.basis}</div>}
                {m.needs && m.needs.length > 0 && (
                  <div className="mt-0.5 text-xs text-amber-700">Needs: {m.needs.join(', ')}</div>
                )}
                <PriceScenarios
                  scenarios={m.scenarios}
                  format={(value) => value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
